import React, { useRef, useState } from "react";

function formatTime(s) {
  if (!isFinite(s)) return "0:00";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60).toString().padStart(2, "0");
  return `${m}:${sec}`;
}

export default function PodcastPlayer({
  src,
  artwork,                 // e.g. "/images/talkers.png"
  accent = "#0ea5e9",       // brand light blue
}) {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration]   = useState(0);
  const [current, setCurrent]     = useState(0);
  const [volume, setVolume]       = useState(1);

  // Play / pause
  const togglePlay = () => {
    const a = audioRef.current;
    if (!a) return;
    if (isPlaying) {
      a.pause();
      setIsPlaying(false);
    } else {
      a.play().then(() => setIsPlaying(true)).catch(console.warn);
    }
  };

  // When metadata loaded (duration)
  const onLoaded = () => setDuration(audioRef.current?.duration || 0);

  // Progress updates
  const onTime = () => setCurrent(audioRef.current?.currentTime || 0);

  // Seek via range
  const onSeek = (e) => {
    const a = audioRef.current;
    if (!a) return;
    const value = Number(e.target.value);
    a.currentTime = (value / 100) * duration;
    setCurrent(a.currentTime);
  };

  // Volume
  const onVol = (e) => {
    const v = Number(e.target.value);
    setVolume(v);
    if (audioRef.current) audioRef.current.volume = v;
  };

  // Stop icon when ended
  const onEnded = () => setIsPlaying(false);

  // Spacebar toggles play/pause when card is focused
  const onKeyDown = (e) => {
    if (e.code === "Space") {
      e.preventDefault();
      togglePlay();
    }
  };

  const progressPct = duration ? (current / duration) * 100 : 0;

  return (
    <div
      className="w-full max-w-[560px] mx-auto rounded-2xl shadow-xl bg-white"
      style={{ overflow: "hidden" }}
      tabIndex={0}
      onKeyDown={onKeyDown}
      aria-label="Podcast audio player"
    >
      {/* Square artwork area */}
      <div
        className="relative w-full bg-slate-100"
        style={{ aspectRatio: "1 / 1" }} /* keeps it perfectly square */
      >
        {artwork ? (
          <img
            src={artwork}
            alt=""
            className="w-full h-full object-cover"
            loading="lazy"
          />
        ) : (
          <div
            className="w-full h-full flex items-center justify-center"
            style={{
              background:
                "linear-gradient(135deg, rgba(14,165,233,.15), rgba(34,211,238,.2))",
            }}
          >
            <div className="text-center">
              <div className="text-5xl"></div>
              <div className="mt-2 text-slate-600">Audio</div>
            </div>
          </div>
        )}

        {/* Big play/pause button overlay */}
        <button
          onClick={togglePlay}
          aria-label={isPlaying ? "Pause" : "Play"}
          className="absolute inset-0 m-auto h-16 w-16 rounded-full shadow-lg flex items-center justify-center"
          style={{
            background: "rgba(255,255,255,.9)",
            color: "#0f172a",
            border: "1px solid rgba(15,23,42,.1)",
          }}
        >
          {isPlaying ? (
            <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
              <rect x="6" y="5" width="4" height="14" rx="1"></rect>
              <rect x="14" y="5" width="4" height="14" rx="1"></rect>
            </svg>
          ) : (
            <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
              <path d="M8 5v14l11-7z"></path>
            </svg>
          )}
        </button>
      </div>



        {/* Progress */}
        <div className="mt-4">
          <input
            type="range"
            min="0"
            max="100"
            value={progressPct}
            onChange={onSeek}
            aria-label="Seek"
            className="w-full accent-sky-500"
            style={{
              cursor: "pointer",
              appearance: "none",
              height: "6px",
              borderRadius: "999px",
              background: `linear-gradient(90deg, ${accent} ${progressPct}%, #e5e7eb ${progressPct}%)`,
            }}
          />
          <div className="mt-1 flex justify-between text-xs text-slate-500">
            <span>{formatTime(current)}</span>
            <span>{formatTime(duration)}</span>
          </div>
        </div>

        {/* Transport + volume */}
        <div className="mt-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <button
              onClick={togglePlay}
              className="px-4 py-2 rounded-lg text-white"
              style={{ background: accent, boxShadow: "0 2px 10px rgba(14,165,233,.35)" }}
            >
              {isPlaying ? "Pause" : "Play"}
            </button>
          </div>

          <div className="flex items-center gap-2">
            <svg width="18" height="18" viewBox="0 0 24 24" className="text-slate-600">
              <path fill="currentColor" d="M3 9v6h4l5 5V4L7 9H3z"></path>
            </svg>
            <input
              type="range"
              min="0"
              max="1"
              step="0.01"
              value={volume}
              onChange={onVol}
              aria-label="Volume"
              className="w-28 accent-sky-500"
              style={{ cursor: "pointer" }}
            />
          </div>
        </div>

      {/* The real audio element (hidden) */}
      <audio
        ref={audioRef}
        src={src}
        onLoadedMetadata={onLoaded}
        onTimeUpdate={onTime}
        onEnded={onEnded}
        preload="metadata"
      />
    </div>
  );
}
